import React, { useState, useEffect } from 'react';
import { Alert, Modal, Form, Button, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useReadContract, useWriteContract } from 'wagmi';
import { parseEther, formatEther } from 'viem'; // To handle Ether to Wei conversion
import communityconsensusContractABI from '../../contracts/tgetherCommunityConsensus.json';

const communityconsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;


function SetConsensusModal({ show, setShow, communityName }: { show: boolean, setShow: (show: boolean) => void, communityName: string }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [transactionPending, setTransactionPending] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    numReviewsForAcceptance: '',
    credsNeededForReview: '',
    percentAcceptsNeeded: '',
    consensusTime: '',
    consensusTypes: '',
  });

  const navigate = useNavigate();

  // Fetch the total fee from the contract using useReadContract
  const { data: feeData } = useReadContract({
    address: communityconsensusContractAddress,
    abi: communityconsensusContractABI.abi,
    functionName: 'totalFee',
  });

  useEffect(() => {
    if (feeData) {
      const feeInEther = formatEther(BigInt(feeData as string)); // Convert the fee to Ether for display
    }
  }, [feeData]);

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  // Prepare wagmi's useWriteContract for the write operation
  const { writeContract: createConsensusProposalWrite, data: transactionData, isSuccess } = useWriteContract();

  // Handle form submission
  const handleSetValues = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const consensusTypesArray = createFormData.consensusTypes.split(',').map(type => type.trim());

      await createConsensusProposalWrite({
        abi: communityconsensusContractABI.abi,
        address: communityconsensusContractAddress,
        functionName: 'setCCParams',
        args: [
          communityName,
          Number(createFormData.numReviewsForAcceptance),
          Number(createFormData.credsNeededForReview),
          Number(createFormData.percentAcceptsNeeded),
          Number(createFormData.consensusTime),
          consensusTypesArray,
        ],
      });

      // Show "Transaction pending" alert
      setTransactionPending(true);
    } catch (error) {
      console.error('Error creating consensus proposal:', error);
      setShowSuccessAlert(false);
    }
  };

  // Close modal and navigate on success
  useEffect(() => {
    if (isSuccess) {
      setShow(false); // Close the modal
      setTransactionPending(false); // Stop showing the pending message
      setShowSuccessAlert(true); // Show success message

      // Navigate to the community search results
      navigate(`/search-results?type=Community&value=${encodeURIComponent(communityName)}`);
    }
  }, [isSuccess, setShow, navigate, communityName]);

  const renderTooltip = (text: string) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Group>
            <Form.Label>
              Number of Reviews for Acceptance
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('This value sets the number of reviews a proposal must receive for acceptance.')}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="numReviewsForAcceptance"
              value={createFormData.numReviewsForAcceptance}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 2:
        return (
          <Form.Group>
            <Form.Label>
              Creds Needed for Review
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('This value sets the credentials a member needs to review proposals.')}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="credsNeededForReview"
              value={createFormData.credsNeededForReview}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 3:
        return (
          <Form.Group>
            <Form.Label>
              Percentage of Accepts Needed
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('This value sets the percentage of accepts needed for a proposal to pass.')}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="percentAcceptsNeeded"
              value={createFormData.percentAcceptsNeeded}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 4:
        return (
          <Form.Group>
            <Form.Label>
              Consensus Time (in seconds)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('This value sets the time a proposal will remain active for consensus.')}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="consensusTime"
              value={createFormData.consensusTime}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 5:
        return (
          <Form.Group>
            <Form.Label>
              Consensus Types (comma-separated)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('Enter the types of consensus mechanisms applicable to this proposal, separated by commas.')}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              name="consensusTypes"
              value={createFormData.consensusTypes}
              onChange={handleCreateInputChange}
              placeholder="Type1,Type2,Type3"
            />
          </Form.Group>
        );
    }
  };

  return (
    <>
      {transactionPending && (
        <Alert variant="info">
          Transaction pending...
        </Alert>
      )}
      {showSuccessAlert && (
        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
          Consensus parameters set successfully!
        </Alert>
      )}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ textDecoration: 'strong' }}>Create a Consensus Proposal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSetValues}>
            {renderStep()}
            <ProgressBar now={(currentStep / 5) * 100} className="my-3" />
            <div className="d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                Previous
              </Button>
              {currentStep < 5 ? (
                <Button variant="info" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button variant="primary" type="submit">
                  Set Consensus Parameters
                </Button>
              )}
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default SetConsensusModal;
